import type { ContactImportItemDto } from '@kardume/types';

/**
 * Leitura de contatos colados como texto ou vindos de uma planilha (CSV/TSV).
 *
 * Mora no navegador de propósito: é aqui que o operador vê o resultado antes de
 * enviar. Se o backend recebesse o arquivo cru, ele teria de adivinhar separador,
 * codificação e cabeçalho - e um palpite errado só apareceria como "importei 0
 * contatos", sem dizer por quê. Assim, o backend recebe linhas já estruturadas e
 * responde apenas sobre o que é regra de negócio (telefone válido, duplicado).
 */

type ContactField = keyof Omit<ContactImportItemDto, 'attributes'>;

export interface ParsedContacts {
  items: ContactImportItemDto[];
  /** Linhas descartadas por estarem em branco. */
  skipped: number;
  /** Cabeçalhos que viraram tags livres (colunas extras da planilha). */
  extraColumns: string[];
  /** Verdadeiro quando a primeira linha foi tratada como cabeçalho. */
  headerDetected: boolean;
  /** Linhas sem telefone reconhecido - o backend vai recusar todas elas. */
  missingPhone: number;
  /**
   * Linhas sem nome. Não são recusadas: o backend usa a empresa e, na falta
   * dela, o próprio WhatsApp. Contadas só para a tela poder avisar.
   */
  missingName: number;
}

/**
 * Apelidos de cada campo, **em ordem de especificidade**: quando duas colunas caem
 * no mesmo campo (`Telefone` e `Celular`), a de apelido mais específico ganha e a
 * outra vira tag, em vez de uma sobrescrever a outra em silêncio.
 */
const FIELD_ALIASES: Record<ContactField, string[]> = {
  name: ['nome', 'name', 'contato', 'responsavel', 'lojista'],
  phoneNumber: ['whatsapp', 'celular', 'telefone', 'fone', 'phone', 'zap', 'numero'],
  company: ['empresa', 'company', 'estabelecimento', 'razaosocial', 'fantasia', 'loja'],
  city: ['cidade', 'city', 'municipio'],
  segment: ['segmento', 'segment', 'ramo', 'categoria'],
};

const CONTACT_FIELDS = Object.keys(FIELD_ALIASES) as ContactField[];

/** Minúscula, sem acento, sem pontuação - para comparar cabeçalhos. */
const normalize = (value: string): string =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');

/** Um telefone tem pelo menos 8 dígitos; é o que distingue dado de cabeçalho. */
const looksLikePhone = (value: string): boolean => value.replace(/\D/g, '').length >= 8;

/**
 * Quebra o texto em registros respeitando aspas.
 *
 * Um `split(/\r?\n/)` cru parte no meio de um campo entre aspas - endereço com
 * quebra de linha é comum em exportação do Excel - e produz um registro truncado
 * mais uma linha fantasma que o backend recusa sem explicar por quê.
 */
function splitRecords(text: string): string[] {
  const records: string[] = [];
  let current = '';
  let quoted = false;
  for (let i = 0; i < text.length; i += 1) {
    const char = text[i];
    if (char === '"') {
      if (quoted && text[i + 1] === '"') {
        current += '""';
        i += 1;
      } else {
        quoted = !quoted;
        current += char;
      }
      continue;
    }
    if (!quoted && (char === '\n' || char === '\r')) {
      if (char === '\r' && text[i + 1] === '\n') i += 1;
      records.push(current);
      current = '';
      continue;
    }
    current += char;
  }
  records.push(current);
  return records;
}

/** Conta o separador só fora das aspas - dentro delas ele é texto. */
function countOutsideQuotes(line: string, delimiter: string): number {
  let count = 0;
  let quoted = false;
  for (let i = 0; i < line.length; i += 1) {
    const char = line[i];
    if (char === '"') {
      if (quoted && line[i + 1] === '"') i += 1;
      else quoted = !quoted;
    } else if (char === delimiter && !quoted) {
      count += 1;
    }
  }
  return count;
}

/**
 * O separador é o candidato que aparece mais na primeira linha, contado **fora das
 * aspas**. Contar dentro faria `"Bar, Restaurante, Ltda";41 99999-0001` escolher a
 * vírgula e a linha inteira viraria um só campo, sem telefone.
 */
function detectDelimiter(firstLine: string): string {
  const candidates = [';', '\t', ','];
  let best = ';';
  let bestCount = 0;
  candidates.forEach((candidate) => {
    const count = countOutsideQuotes(firstLine, candidate);
    if (count > bestCount) {
      best = candidate;
      bestCount = count;
    }
  });
  return best;
}

/** Divide uma linha respeitando aspas duplas (o Excel exporta assim). */
function splitRow(line: string, delimiter: string): string[] {
  const cells: string[] = [];
  let current = '';
  let quoted = false;
  for (let i = 0; i < line.length; i += 1) {
    const char = line[i];
    if (char === '"') {
      if (quoted && line[i + 1] === '"') {
        current += '"';
        i += 1;
      } else {
        quoted = !quoted;
      }
    } else if (char === delimiter && !quoted) {
      cells.push(current);
      current = '';
    } else {
      current += char;
    }
  }
  cells.push(current);
  return cells.map((cell) => cell.trim());
}

/**
 * A primeira linha é cabeçalho quando alguma célula tem nome de campo conhecido e
 * nenhuma célula parece um telefone. Sem a segunda condição, uma planilha sem
 * cabeçalho cujo primeiro contato se chama "Nome Sobrenome" perderia esse contato.
 */
function isHeader(cells: string[]): boolean {
  const known = CONTACT_FIELDS.flatMap((field) => FIELD_ALIASES[field]);
  const hasKnownLabel = cells.some((cell) => known.includes(normalize(cell)));
  return hasKnownLabel && !cells.some(looksLikePhone);
}

function fieldFor(header: string): ContactField | null {
  const normalized = normalize(header);
  return CONTACT_FIELDS.find((field) => FIELD_ALIASES[field].includes(normalized)) ?? null;
}

/** Posição do apelido na lista do campo: menor = mais específico. */
const aliasRank = (field: ContactField, header: string): number =>
  FIELD_ALIASES[field].indexOf(normalize(header));

/**
 * Converte texto colado ou conteúdo de CSV/TSV em linhas prontas para importar.
 *
 * Sem cabeçalho, assume `Nome; WhatsApp`, mas quem *parece* telefone é tratado como
 * telefone: colunas invertidas ou uma coluna só de números são erros comuns, e o
 * palpite literal transformaria a planilha inteira em "WhatsApp inválido".
 */
export function parseContacts(text: string): ParsedContacts {
  const rawRecords = splitRecords(text);
  const contentLines = rawRecords.filter((line) => line.trim().length > 0);
  const skipped = rawRecords.length - contentLines.length;

  if (contentLines.length === 0) {
    return {
      items: [],
      skipped,
      extraColumns: [],
      headerDetected: false,
      missingPhone: 0,
      missingName: 0,
    };
  }

  const delimiter = detectDelimiter(contentLines[0]);
  const rows = contentLines.map((line) => splitRow(line, delimiter));

  const headerDetected = isHeader(rows[0]);
  const headers = headerDetected ? rows[0] : [];
  const dataRows = headerDetected ? rows.slice(1) : rows;
  const extraColumns: string[] = [];

  // Mapa coluna → campo, resolvido uma vez para toda a planilha.
  const mapped: (ContactField | null)[] = headers.map(fieldFor);

  // Cabeçalho de telefone fora da lista de apelidos ("Celular (WhatsApp)", "Nº Fone"):
  // a coluna cujos dados parecem telefone é o telefone. Sem isso ela viraria uma tag
  // e 100% das linhas voltariam do backend como "WhatsApp inválido".
  if (headerDetected && !mapped.includes('phoneNumber')) {
    const phoneIndex = headers.findIndex((_, index) =>
      dataRows.some((cells) => looksLikePhone(cells[index] ?? ''))
    );
    if (phoneIndex >= 0) mapped[phoneIndex] = 'phoneNumber';
  }

  const items = dataRows.map((cells) => {
    const item: ContactImportItemDto = { attributes: {} };

    if (!headerDetected) {
      const [first = '', second = ''] = cells;
      if (looksLikePhone(first) && !looksLikePhone(second)) {
        item.phoneNumber = first || undefined;
        item.name = second || undefined;
      } else {
        item.name = first || undefined;
        item.phoneNumber = second || undefined;
      }
      return item;
    }

    // Ranking por campo: se duas colunas disputam o mesmo campo, a de apelido mais
    // específico fica e a outra vira tag - em vez de a última sobrescrever a primeira.
    const claimed = new Map<ContactField, number>();
    headers.forEach((header, index) => {
      const value = (cells[index] ?? '').trim();
      if (!value) return;
      const field = mapped[index];
      const rank = field ? aliasRank(field, header) : Number.MAX_SAFE_INTEGER;
      const currentRank = field ? claimed.get(field) : undefined;
      if (field && (currentRank === undefined || rank < currentRank)) {
        claimed.set(field, rank);
        item[field] = value;
      } else {
        item.attributes = { ...item.attributes, [header]: value };
        if (!extraColumns.includes(header)) extraColumns.push(header);
      }
    });
    return item;
  });

  return {
    items,
    skipped,
    extraColumns,
    headerDetected,
    // Contados aqui para a tela poder avisar ANTES de o operador clicar em Importar.
    missingPhone: items.filter((item) => !item.phoneNumber?.trim()).length,
    missingName: items.filter((item) => !item.name?.trim()).length,
  };
}
